import "./global.ts";

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import {
  createBrowserRouter,
  RouterProvider,
  LoaderFunctionArgs,
} from 'react-router-dom';

// create shadow root, must be done before importing any stylesheets
import rootElement from './rootElement';

import './fontAwesome';

import '@fortawesome/fontawesome-svg-core/styles.css';

import reportWebVitals from './reportWebVitals';

import store, {
  fetchOrderInfo,
  fetchAPIData,
  fetchAPIVrData,
  setActiveModel,
  setIsEmbedded,
} from './store';

import AppLoader from './AppLoader';

const WidgetElement = document.getElementById('visrez-interactive');
if (!WidgetElement) {
  throw new Error('Missing #visrez-interactive div element');
}

// Fix for embed without setting height
if (WidgetElement.clientHeight === 0) {
  WidgetElement.style.height = '600px';
}

const dataset = WidgetElement.dataset;

// Determine if we are embedded on a client page
const isEmbedded = dataset.client !== undefined;

async function loadMapAPIData({ request, params }: LoaderFunctionArgs) {
  const searchParams = new URL(request.url).searchParams;
  const cover = isEmbedded ? dataset.cover : searchParams.get('cover');
  store.dispatch(setIsEmbedded(isEmbedded));
  const orderId = isEmbedded ? dataset.client : params.orderId;
  if (!orderId) {
    if (isEmbedded) {
      throw new Error('Missing #visrez-interactive data-client attribute');
    } else {
      throw new Error('Bad URL');
    }
  }
  const mapId = (isEmbedded ? dataset.map : params.mapId) ?? 'first';
  await store.dispatch(fetchOrderInfo({ orderId, mapId }));
  const layerId = isEmbedded ? dataset.layer : params.layerId;
  const spaceId = isEmbedded ? dataset.space : params.spaceId;
  const openSpace = (isEmbedded ? dataset.openSpace : params.openSpace) === 'open';
  const openCategory = isEmbedded ? dataset.category : params.categoryId;
  const primaryColor = isEmbedded ? dataset.menuHoverColor : null;
  const secondaryColor = isEmbedded ? dataset.menuColor : null;
  const backgroundColor = isEmbedded ? dataset.backgroundColor : null;
  const loaderBackgroundColor = isEmbedded ? dataset.loaderBackgroundColor : null;
  const loaderLogoBackgroundColor = isEmbedded ? dataset.loaderLogoBackgroundColor : null;
  const forceLoader = isEmbedded ? dataset.forceLoader === 'true' : false;

  if (!cover) { // Cover don't need anything else
    store.dispatch(fetchAPIData({ orderId, mapId }));
  }
  return {
    isVr: false, layerId, spaceId, openSpace, openCategory,
    primaryColor, secondaryColor, backgroundColor,
    loaderBackgroundColor, loaderLogoBackgroundColor, forceLoader,
    cover,
  };
}

async function loadVrAPIData({ request, params }: LoaderFunctionArgs) {
  const searchParams = new URL(request.url).searchParams;
  let cover = isEmbedded ? dataset.cover : searchParams.get('cover');
  // Keep old vrCover param working, remove when it's not used anymore
  if (!cover) {
    if (dataset.vrCover === 'true' || searchParams.get('vrCover') === 'true') {
      cover = 'basic';
    }
  }
  store.dispatch(setIsEmbedded(isEmbedded));
  const orderId = isEmbedded ? dataset.client : params.orderId;
  if (!orderId) {
    if (isEmbedded) {
      throw new Error('Missing #visrez-interactive data-client attribute');
    } else {
      throw new Error('Bad URL');
    }
  }
  const vrId = isEmbedded ? dataset.vr : params.vrId;
  const viewId = isEmbedded ? dataset.view : params.viewId;
  await store.dispatch(fetchOrderInfo({ orderId, vrId }));
  const loaderBackgroundColor = isEmbedded ? dataset.loaderBackgroundColor : null;
  const loaderLogoBackgroundColor = isEmbedded ? dataset.loaderLogoBackgroundColor : null;
  const forceLoader = isEmbedded ? dataset.forceLoader === 'true' : false;

  if (!cover) { // Cover don't need anything else
    store.dispatch(setActiveModel(Number(vrId)));
    store.dispatch(fetchAPIVrData({ orderId, vrId: vrId! }));
  }
  return {
    isVr: true, viewId, cover,
    loaderBackgroundColor, loaderLogoBackgroundColor, forceLoader,
  };
}

// Rendered while the loader runs on page load: nothing, as in React Router v6
const HydrateFallback = () => null;

const router = createBrowserRouter(
  isEmbedded ? [
    // Routes for app embedded on client pages
    // We can't rely on path here, only on dataset attributes
    {
      path: '*',
      element: <AppLoader />,
      HydrateFallback,
      loader: (
        dataset.vr ? loadVrAPIData : loadMapAPIData
      ),
    },
  ] : [
    // When not embedded, use standard routing
    {
      path: '',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId/:vrId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadVrAPIData,
    },
    {
      path: '/:orderId/:vrId/view/:viewId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadVrAPIData,
    },
    {
      path: '/:orderId/map/:mapId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId/map/:mapId/layer/:layerId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId/map/:mapId/layer/:layerId/space/:spaceId/:openSpace?',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId/map/:mapId/cat/:categoryId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    },
    {
      path: '/:orderId/map/:mapId/layer/:layerId/cat/:categoryId',
      element: <AppLoader />,
      HydrateFallback,
      loader: loadMapAPIData,
    }
  ]
);

const reactRoot = createRoot(rootElement);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: Infinity,
    },
  },
});

reactRoot.render(
  <StrictMode>
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ReduxProvider>
  </StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
